// v2 Donations table — per-donor AI character thumbnail, deterministic voice
// assignment, AI badge if the donor has been generated. Filterable by faction
// and "has message", paginated 50/page.

import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useSession } from "../../lib/session";
import { api } from "../../lib/api";
import { aiCharImage, normHandle } from "../../lib/cast";
import { voiceForHandle } from "../../lib/voices";
import "./dashboard.css";

interface DonationRow {
  id: string;
  donor_name: string;
  donor_handle?: string;
  amount: number;
  message: string | null;
  faction_id: string | null;
  source?: "live" | "profile";
  created_at: number;
}

interface Faction {
  id: string;
  slug: string;
  name: string;
  color: string;
}

interface ProfileResp {
  factions: Faction[];
  donations: DonationRow[];
}

const PAGE_SIZE = 50;

function fmtTime(ts: number): string {
  return new Date(ts).toLocaleString(undefined, {
    year: "2-digit",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// "AI generated" heuristic: until donations API exposes character_prompt /
// imagen_url, we surface the badge for every donation with a message (those
// are the ones that get spoken in the cast). TODO wire to real field.
function hasAiCharacter(d: DonationRow): boolean {
  return !!d.message && d.message.trim().length > 0;
}

export default function DashboardDonations(): JSX.Element {
  const user = useSession((s) => s.user);
  const loading = useSession((s) => s.loading);
  const [search, setSearch] = useState("");
  const [factionFilter, setFactionFilter] = useState<string>("");
  const [amountFilter, setAmountFilter] = useState<string>("");
  const [hasMessage, setHasMessage] = useState<string>("");
  const [page, setPage] = useState(0);

  const q = useQuery({
    queryKey: ["dashboard-donations", user?.handle],
    queryFn: () => api.get<ProfileResp>(`/api/streamer/${user?.handle}`),
    enabled: !!user?.handle,
  });

  const factionMap = useMemo(
    () => new Map((q.data?.factions ?? []).map((f) => [f.id, f])),
    [q.data],
  );

  const filtered = useMemo(() => {
    const rows = q.data?.donations ?? [];
    const needle = search.trim().toLowerCase();
    const minAmt = amountFilter === "1+" ? 1 : amountFilter === "10+" ? 10 : amountFilter === "50+" ? 50 : amountFilter === "100+" ? 100 : 0;
    return rows.filter((r) => {
      if (factionFilter && r.faction_id !== factionFilter) return false;
      if (minAmt && r.amount < minAmt) return false;
      if (hasMessage === "yes" && !r.message) return false;
      if (hasMessage === "no" && r.message) return false;
      if (!needle) return true;
      const hay = `${r.donor_name} ${r.message ?? ""}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [q.data, search, factionFilter, amountFilter, hasMessage]);

  const pageRows = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  if (loading) return <p style={{ padding: 20 }}>Loading…</p>;
  if (!user) return <Navigate to="/login?returnUrl=/dashboard/donations" replace />;

  return (
    <>
      <div className="dash-page-head">
        <div>
          <h1 className="dash-page-title">Donations</h1>
          <p className="dash-page-sub">your cast members · every donor + their AI character</p>
        </div>
      </div>

      <div className="dash-card elev">
        <div className="dash-filters">
          <input
            type="search"
            placeholder="Search donor or message…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            className="dash-input"
          />
          <select
            value={factionFilter}
            onChange={(e) => { setFactionFilter(e.target.value); setPage(0); }}
            className="dash-input"
          >
            <option value="">All factions</option>
            {(q.data?.factions ?? []).map((f) => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
          <select
            value={amountFilter}
            onChange={(e) => { setAmountFilter(e.target.value); setPage(0); }}
            className="dash-input"
          >
            <option value="">Any amount</option>
            <option value="1+">$1+</option>
            <option value="10+">$10+</option>
            <option value="50+">$50+</option>
            <option value="100+">$100+</option>
          </select>
          <select
            value={hasMessage}
            onChange={(e) => { setHasMessage(e.target.value); setPage(0); }}
            className="dash-input"
          >
            <option value="">All messages</option>
            <option value="yes">With message</option>
            <option value="no">No message</option>
          </select>
        </div>

        <div className="dash-card-sub" style={{ marginBottom: 10 }}>
          {filtered.length} donations
          {filtered.length !== (q.data?.donations.length ?? 0)
            ? ` (filtered from ${q.data?.donations.length ?? 0})`
            : ""}
        </div>

        {q.isLoading ? (
          <p style={{ color: "var(--text-mute)" }}>Loading donations…</p>
        ) : pageRows.length === 0 ? (
          <p style={{ color: "var(--text-mute)" }}>No donations match.</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="dash-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Donor</th>
                  <th>Amount</th>
                  <th>Faction</th>
                  <th>Message</th>
                  <th>Voice</th>
                  <th title="AI character generated">AI</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((d) => {
                  const fac = d.faction_id ? factionMap.get(d.faction_id) : null;
                  const handle = normHandle(d.donor_handle ?? d.donor_name);
                  const voice = voiceForHandle(handle);
                  const hasAi = hasAiCharacter(d);
                  return (
                    <tr key={d.id}>
                      <td>{fmtTime(d.created_at)}</td>
                      <td>
                        <div className="dash-donor-cell">
                          <div className="dash-donor-thumb">
                            <img
                              src={aiCharImage(handle)}
                              alt=""
                              loading="lazy"
                              onError={(e) => { e.currentTarget.style.display = "none"; }}
                            />
                          </div>
                          <div>
                            <div className="dash-donor-name">{d.donor_name}</div>
                            <div className="dash-donor-handle">@{handle}</div>
                          </div>
                        </div>
                      </td>
                      <td className="amt">${d.amount.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                      <td>
                        {fac ? (
                          <span style={{ color: fac.color }}>
                            <span className="dash-faction-dot" style={{ background: fac.color }} />
                            {fac.name}
                          </span>
                        ) : (
                          <span style={{ color: "var(--text-mute)" }}>—</span>
                        )}
                      </td>
                      <td className="msg" title={d.message ?? ""}>{d.message ?? ""}</td>
                      <td>
                        <span style={{ fontFamily: "var(--f-mono)", fontSize: 12 }}>
                          {voice.name}
                          <span style={{ color: "var(--text-mute)" }}> · {voice.vibe}</span>
                        </span>
                      </td>
                      <td>
                        <span
                          className={`dash-ai-badge${hasAi ? "" : " off"}`}
                          title={hasAi ? "AI character generated" : "No AI character"}
                        >
                          ⌬
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 ? (
          <div className="dash-pager">
            <button
              className="dash-btn"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              ← Prev
            </button>
            <span className="dash-pager-info">Page {page + 1} / {totalPages}</span>
            <button
              className="dash-btn"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
            >
              Next →
            </button>
          </div>
        ) : null}
      </div>
    </>
  );
}
